import { ROUTES } from '../constants/urls';

const PROPERTY_NAV_CACHE_KEY = 'zrm_property_nav_address';
const PORTFOLIO_ORIGIN_KEY = 'zrm_portfolio_origin';
const CREATE_LISTING_NAV_KEY = 'zrm_create_listing_is_new';

export type CachedPropertyAddress = {
  street: string;
  city: string;
  state: string;
  unit: string;
  zip: string;
};

// Keyed by listing so a reload (sessionStorage outlives it) can't show the previously
// visited property's address on a different property's dashboard.
type CachedPropertyAddressEntry = {
  listingAlias: string;
  address: CachedPropertyAddress;
};

export function cachePropertyAddress(listingAlias: string, address: CachedPropertyAddress): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(PROPERTY_NAV_CACHE_KEY, JSON.stringify({ listingAlias, address }));
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}

export function getCachedPropertyAddress(listingAlias: string): CachedPropertyAddress | null {
  if (typeof window === 'undefined' || !listingAlias) return null;
  try {
    const raw = sessionStorage.getItem(PROPERTY_NAV_CACHE_KEY);
    if (!raw) return null;

    const entry = JSON.parse(raw) as CachedPropertyAddressEntry;
    return entry?.listingAlias === listingAlias ? entry.address : null;
  } catch {
    return null;
  }
}

export function setIsNewListing(isNew: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(CREATE_LISTING_NAV_KEY, String(isNew));
  } catch {
    // Silently fail if sessionStorage unavailable
  }
}

export function getIsNewListing(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const value = sessionStorage.getItem(CREATE_LISTING_NAV_KEY);
    return value === 'true';
  } catch {
    // Silently fail
  }
  return false;
}

export function clearIsNewListing(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(CREATE_LISTING_NAV_KEY);
  } catch {
    // Silently fail
  }
}

export const PORTFOLIO_ORIGIN = { listings: 'listings', properties: 'properties' } as const;

export type PortfolioOrigin = (typeof PORTFOLIO_ORIGIN)[keyof typeof PORTFOLIO_ORIGIN];

// Written by each list page on mount, read by the layout.
export function setPortfolioOrigin(origin: PortfolioOrigin): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(PORTFOLIO_ORIGIN_KEY, origin);
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
}

export function getPortfolioOrigin(): PortfolioOrigin | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = sessionStorage.getItem(PORTFOLIO_ORIGIN_KEY);
    return value === PORTFOLIO_ORIGIN.listings || value === PORTFOLIO_ORIGIN.properties ? value : null;
  } catch {
    return null;
  }
}

// Detail routes only: on /properties itself the stored origin would flash the wrong item.
export function isListingsNavContext(routePathname: string): boolean {
  return routePathname.startsWith(`${ROUTES.PROPERTIES}/[`) && getPortfolioOrigin() === PORTFOLIO_ORIGIN.listings;
}
